import { Link } from "react-router-dom";
import {
    ArrowRight,
    Smartphone,
    Coffee,
    Building2,
    Sparkles,
    CheckCircle2,
    Zap,
    MapPin,
    Snowflake,
    Repeat,
    CreditCard,
} from "lucide-react";
import useSeo from "@/hooks/useSeo";
import { SITE_IMAGES } from "@/lib/siteAssets";

const SEO_JSON_LD = [
    {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: "Vendagain",
        description:
            "Free vending machine placement (vending machine hire) for Perth workplaces — snacks, drinks and combo machines. Also custom phone case printing vending machines for high-traffic malls, shopping centres and airports.",
        areaServed: [
            { "@type": "AdministrativeArea", name: "Western Australia" },
            { "@type": "City", name: "Perth" },
        ],
        address: {
            "@type": "PostalAddress",
            addressLocality: "Perth",
            addressRegion: "WA",
            addressCountry: "AU",
        },
        telephone: "+61410784187",
        email: "info@vendagain.com.au",
        priceRange: "Free placement*",
        url: "https://vendagain.com.au",
    },
    {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Vendagain",
        url: "https://vendagain.com.au",
        sameAs: ["https://www.tiktok.com/@vendagain"],
        contactPoint: [
            {
                "@type": "ContactPoint",
                telephone: "+61-410-784-187",
                contactType: "customer service",
                email: "info@vendagain.com.au",
                areaServed: "AU",
                availableLanguage: "en",
            },
        ],
    },
];

export default function HomePage() {
    useSeo({
        title: "Vending Machines Perth — Free Placement for Workplaces",
        description:
            "Vendagain provides free snack & drink vending machine placement — commonly known as vending machine hire — for Perth offices, schools, factories, warehouses and construction sites. Weekly restocks, product monitoring, summer pre-chill. Plus custom phone case printing vending machines for malls and airports.",
        canonicalPath: "/",
        keywords:
            "vending machines Perth, vending machine hire Perth, free vending machine hire Perth, free vending machine Perth, office vending machines Perth, workplace vending Perth WA, snack vending Perth, drink vending Perth, phone case vending machine Perth, custom phone case Perth",
        jsonLd: SEO_JSON_LD,
    });

    return (
        <>
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 lg:pt-20 pb-14 lg:pb-24 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 text-green-700 text-[11px] font-semibold uppercase tracking-wider mb-5">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                            Perth & Western Australia
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.02]" data-testid="home-hero-title">
                            Perth's vending machines — done properly.
                        </h1>
                        <p className="mt-5 text-lg text-neutral-600 leading-relaxed max-w-xl">
                            Free snack & drink vending machine placement for
                            Perth workplaces. Weekly fills. Real product
                            monitoring. Pre-chilled drinks in summer. Plus
                            custom phone case printing machines for high-traffic
                            venues.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/host-a-machine#form" className="vg-pill vg-pill-accent" data-testid="home-cta-host">
                                Get a free vending machine
                                <ArrowRight size={14} />
                            </Link>
                            <Link to="/designer" className="vg-pill vg-pill-outline" data-testid="home-cta-designer">
                                <Smartphone size={16} />
                                Try the Case Designer
                            </Link>
                        </div>

                        <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm text-neutral-500">
                            <div className="flex items-center gap-1.5">
                                <CheckCircle2 size={16} className="text-green-500" />
                                Free placement for eligible sites
                            </div>
                            <div className="flex items-center gap-1.5">
                                <CheckCircle2 size={16} className="text-green-500" />
                                Weekly restocks
                            </div>
                            <div className="flex items-center gap-1.5">
                                <CheckCircle2 size={16} className="text-green-500" />
                                Perth-based team
                            </div>
                        </div>
                    </div>

                    <div className="relative">
                        <div className="grid grid-cols-2 gap-4 lg:gap-5">
                            <HeroCard
                                accent="bg-green-500"
                                icon={<Coffee size={20} />}
                                title="Food & Drink"
                                subtitle="Free placement for offices, factories & warehouses"
                                to="/food-drink-vending"
                            />
                            <HeroCard
                                accent="bg-neutral-950"
                                icon={<Smartphone size={20} />}
                                title="Phone Cases"
                                subtitle="Custom prints in minutes at malls & airports"
                                to="/phone-case-vending"
                                dark
                            />
                            <HeroCard
                                accent="bg-neutral-950"
                                icon={<Building2 size={20} className="text-white" />}
                                title="Host a Machine"
                                subtitle="Free install for Perth workplaces"
                                to="/host-a-machine"
                                dark
                            />
                            <HeroCard
                                accent="bg-green-500"
                                icon={<Sparkles size={20} />}
                                title="Case Designer"
                                subtitle="Preview your design before you print"
                                to="/designer"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="border-t border-neutral-200 bg-neutral-50/50 py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="max-w-2xl mb-12">
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            What we do
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="home-services-title">
                            Two machines. One promise: done properly.
                        </h2>
                        <p className="mt-3 text-neutral-600 leading-relaxed">
                            We only install machines we can service properly.
                            That means Perth-wide, weekly, and with real data
                            behind every product decision.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <ServiceCard
                            eyebrow="Free placement — where the leads matter"
                            title="Food & Drink Vending"
                            body="Free machine, free install, free servicing for Perth offices, schools, factories, warehouses and construction sites. We use Royal Vending equipment — cashless or coin/note options — and stock changes weekly based on what your team actually buys."
                            to="/food-drink-vending"
                            accent="green"
                        />
                        <ServiceCard
                            eyebrow="Star Product"
                            title="Custom Phone Case Printing"
                            body="A DIY vending machine that prints any image from a customer's phone onto a premium case in minutes. Perfect for high-traffic malls, shopping centres and airports — we lease the site from you and drive walk-ins."
                            to="/phone-case-vending"
                            accent="dark"
                        />
                    </div>
                </div>
            </section>

            {/* USPs */}
            <section className="py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="max-w-2xl mb-12">
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            Why Vendagain
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">
                            Not the sticky, half-empty vending machine you remember.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        <UspCard
                            icon={<Repeat size={20} />}
                            title="Weekly fills"
                            body="Every machine, every week. No months-long gaps between visits. If it's empty, we hear about it fast — and fix it faster."
                        />
                        <UspCard
                            icon={<Sparkles size={20} />}
                            title="Product monitoring"
                            body="We watch what's actually selling at your site and swap out what isn't. You get what your team wants, not what a supplier wants us to shift."
                        />
                        <UspCard
                            icon={<Snowflake size={20} />}
                            title="Summer pre-chill"
                            body="Perth summers are brutal. We pre-chill enough stock so the first two drinks in every row are icy cold from the moment we walk away."
                        />
                        <UspCard
                            icon={<CreditCard size={20} />}
                            title="Cashless-ready"
                            body="Tap, card, phone wallet — every machine accepts modern payments. Food & drink units can also take notes and coins if you want."
                        />
                    </div>
                </div>
            </section>

            {/* HOST A MACHINE CTA */}
            <section className="bg-neutral-950 text-white py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-16 items-center">
                    <div>
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-400">
                            For Perth workplaces
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight leading-tight text-white">
                            A vending machine your team will actually use.
                        </h2>
                        <p className="mt-4 text-neutral-300 leading-relaxed max-w-xl">
                            We install, stock and service — the machine is free
                            for eligible offices, schools, training facilities,
                            factories, warehouses and construction sites. Just
                            give us a small corner and access to power.
                        </p>
                        <div className="mt-8">
                            <Link to="/host-a-machine" className="vg-pill vg-pill-accent">
                                See how it works
                                <ArrowRight size={14} />
                            </Link>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        {[
                            { k: "$0", v: "Free placement" },
                            { k: "Weekly", v: "Stock refills" },
                            { k: "24/7", v: "Available to staff" },
                            { k: "Perth", v: "Local team" },
                        ].map((s) => (
                            <div
                                key={s.v}
                                className="bg-white/5 border border-white/10 rounded-2xl p-5"
                            >
                                <div className="text-2xl font-semibold text-green-400">
                                    {s.k}
                                </div>
                                <div className="text-xs text-neutral-400 mt-1 uppercase tracking-wider">
                                    {s.v}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PHONE CASE TEASER */}
            <section className="py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            Star product
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">
                            Your only limit is your imagination.
                        </h2>
                        <p className="mt-4 text-neutral-600 leading-relaxed">
                            If it's on your phone, it can be on your case. Our
                            DIY phone case vending machines let anyone with a
                            phone walk up, upload any image, and walk away with
                            a one-of-a-kind case in about 3 minutes. Cashless
                            payment. No app. No account.
                        </p>
                        <ul className="mt-6 space-y-2.5 text-sm text-neutral-700">
                            {[
                                "61+ phone models & variations supported",
                                "300 DPI high-resolution print",
                                "About 3 minutes from upload to case",
                                "100% cashless — tap, card, phone wallet",
                                "Magnetic upgrade available on selected models",
                            ].map((t) => (
                                <li key={t} className="flex items-start gap-2">
                                    <CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                                    {t}
                                </li>
                            ))}
                        </ul>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/designer" className="vg-pill vg-pill-primary">
                                Try the designer
                                <ArrowRight size={14} />
                            </Link>
                            <Link to="/phone-case-vending" className="vg-pill vg-pill-outline">
                                Learn more
                            </Link>
                        </div>
                    </div>
                    <div className="relative">
                        <div className="relative aspect-square rounded-3xl overflow-hidden bg-neutral-100 border border-neutral-200">
                            <img
                                src={SITE_IMAGES.caseMachineFront}
                                alt="Vendagain Case Laboratory phone case vending machine in a Perth shopping centre"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                        <div className="absolute -bottom-4 -left-4 bg-white border border-neutral-200 rounded-2xl p-4 shadow-lg max-w-[220px]">
                            <div className="flex items-baseline gap-1.5 mb-1">
                                <Zap size={14} className="text-green-500 self-center" />
                                <span className="text-2xl font-semibold leading-none">3</span>
                                <span className="text-green-600 text-sm font-semibold">min</span>
                            </div>
                            <div className="text-xs text-neutral-500 leading-snug">
                                from upload to a finished case in your hand
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* LOCATIONS TEASER */}
            <section className="py-16 lg:py-20 bg-neutral-50 border-t border-neutral-200">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
                    <MapPin size={28} className="text-green-500 mx-auto mb-3" />
                    <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">
                        Perth-first. Growing fast.
                    </h2>
                    <p className="mt-3 text-neutral-600 max-w-xl mx-auto leading-relaxed">
                        We're actively adding new food & drink machines to
                        Perth workplaces every month — and always on the hunt
                        for premium mall and airport partners for our phone case
                        machines.
                    </p>
                    <div className="mt-8">
                        <Link to="/host-a-machine#form" className="vg-pill vg-pill-primary">
                            Register your interest
                            <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* SCAVENGER HUNT TEASER */}
            <section className="py-14 lg:py-16 bg-neutral-950 text-white border-t border-neutral-900 overflow-hidden relative">
                <div
                    className="absolute inset-0 opacity-40 pointer-events-none"
                    aria-hidden="true"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 15% 50%, rgba(34,197,94,0.18), transparent 50%), radial-gradient(circle at 85% 50%, rgba(34,197,94,0.10), transparent 45%)",
                    }}
                />
                <div className="max-w-5xl mx-auto px-4 sm:px-6 relative grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/15 text-green-400 text-[11px] font-semibold uppercase tracking-widest mb-3 border border-green-500/20">
                            🕵️ Scavenger hunt
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight leading-tight text-white">
                            There's a secret code on our TikTok.
                        </h2>
                        <p className="mt-2 text-neutral-300 text-sm sm:text-base leading-relaxed max-w-xl">
                            Find it, enter it at any Vendagain machine, get{" "}
                            <b className="text-green-400">$5 off your case</b>.
                            Numbers only — you'll know it when you see it.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <a
                            href="https://www.tiktok.com/@vendagain"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="vg-pill vg-pill-accent"
                            data-testid="hunt-home-tiktok"
                        >
                            Watch @vendagain
                        </a>
                        <Link to="/designer" className="vg-pill vg-pill-outline bg-transparent text-white border-white/20 hover:bg-white/10 hover:border-white/40">
                            Test the code
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}

function HeroCard({ icon, title, subtitle, accent, to, dark }) {
    return (
        <Link
            to={to}
            className={`group rounded-2xl p-5 border transition-all hover:-translate-y-1 hover:shadow-lg ${
                dark
                    ? "bg-neutral-950 text-white border-neutral-900"
                    : "bg-white border-neutral-200"
            }`}
        >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 text-white ${accent}`}>
                {icon}
            </div>
            <div className="text-sm font-semibold">{title}</div>
            <div className={`text-xs mt-1 leading-relaxed ${dark ? "text-neutral-400" : "text-neutral-500"}`}>
                {subtitle}
            </div>
            <div className={`text-xs font-medium mt-3 flex items-center gap-1 ${dark ? "text-green-400" : "text-green-600"}`}>
                Explore
                <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" />
            </div>
        </Link>
    );
}

function ServiceCard({ eyebrow, title, body, to, accent }) {
    const isGreen = accent === "green";
    return (
        <Link
            to={to}
            className={`group block rounded-3xl p-8 lg:p-10 border transition-all hover:-translate-y-1 hover:shadow-xl ${
                isGreen ? "bg-white border-neutral-200" : "bg-neutral-950 text-white border-neutral-900"
            }`}
        >
            <span className={`text-xs font-semibold uppercase tracking-widest ${isGreen ? "text-green-600" : "text-green-400"}`}>
                {eyebrow}
            </span>
            <h3 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight leading-tight">
                {title}
            </h3>
            <p className={`mt-4 leading-relaxed ${isGreen ? "text-neutral-600" : "text-neutral-300"}`}>
                {body}
            </p>
            <div className={`mt-6 text-sm font-medium inline-flex items-center gap-1.5 ${isGreen ? "text-green-600" : "text-green-400"}`}>
                Learn more
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </div>
        </Link>
    );
}

function UspCard({ icon, title, body }) {
    return (
        <div className="bg-white border border-neutral-200 rounded-2xl p-6 h-full hover:border-neutral-950 transition-colors">
            <div className="w-10 h-10 rounded-lg bg-green-500 text-white flex items-center justify-center mb-4">
                {icon}
            </div>
            <h3 className="text-base font-semibold mb-2">{title}</h3>
            <p className="text-sm text-neutral-600 leading-relaxed">{body}</p>
        </div>
    );
}
